<template>
  <div 
    class="card" 
    :class="brandClass()">
    <div class="dealer-card-name">
      <div class="dealer-card-name-text" :class="{'disabled' : disabled}">
        {{ dealer?.name || dealer?.dealerName }}
      </div>
      <div 
        :id="title + '-delete-button'"
        @keydown="ariaClick" 
        role="button" 
        tabindex="0" 
        class="dealer-card-name-delete" 
        @click="onDeleteDealerClick(title + '-delete-button')" />
    </div>

    <div 
      v-if="dealer?.street && dealer?.postalCode && dealer?.city" 
      class="dealer-card-street" 
      :class="{'disabled' : disabled}">
      <div class="dealer-card-street-address">
        {{ dealer.street }}
      </div>
      <div class="dealer-card-street-separator">
        |
      </div>
      <div class="dealer-card-street-postal-code-and-city">
        {{ dealer.postalCode }} {{ dealer.city }}
      </div>
    </div>

    <div 
      v-if="dealer?.attributes?.phone" 
      class="dealer-card-phone" 
      :class="{'disabled' : disabled}">
      <div class="dealer-card-phone-icon" />
      <div class="dealer-card-phone-text">
        {{ dealer.attributes.phone }}
      </div>
    </div>

    <div 
      v-if="dealer?.attributes?.fax" 
      class="dealer-card-fax" 
      :class="{'disabled' : disabled}">
      <div class="dealer-card-fax-icon" />
      <div class="dealer-card-fax-text">
        {{ dealer.attributes.fax }}
      </div>
    </div>

    <div 
      v-if="dealer?.attributes?.mail" 
      class="dealer-card-mail" 
      :class="{'disabled' : disabled}">
      <div class="dealer-card-mail-icon" />
      <div class="dealer-card-mail-text">
        {{ dealer.attributes.mail }}
      </div>
    </div>

    <div 
      v-if="dealer?.attributes?.homepage"
      class="dealer-card-homepage" 
      :class="{'disabled' : disabled}">
      <div class="dealer-card-homepage-icon" />
      <div class="dealer-card-homepage-text">
        <a 
          v-if="!disabled"
          :href="dealer.attributes.homepage" 
          target="_blank" 
          rel="noopener noreferrer"
          class="dealer-card-homepage-link">
          {{ removeHttpSubtring(dealer.attributes.homepage) }}
        </a>
        <div v-else class="dealer-card-homepage-link-disabled">
          {{ removeHttpSubtring(dealer.attributes.homepage) }}
        </div>
      </div>
    </div>

    <div 
      v-if="dealer?.attributes?.mail"
      class="buttonsContainer flex_button_container">
      <OneButton
        :brandClass="brandClass()"
        :profileStyle="true"
        :type="'secondary'"
        :label="$t('footer_cta_contact_us_label')"
        @click="onContactDealerButtonClick()"
        :disabled="disabled"
      />
    </div>
  </div>
</template>

<script>
import oneUtils from "../../../gcdm-one-library/gcdm-one-utils/src/bootstrap";
import OneButton from "../../../gcdm-one-library/gcdm-one-button/src/OneButton.vue";

export default {
  inject:['brandClass', '$t', '$c'],
  mixins: [oneUtils.customValidationMixin,oneUtils.accessibilityMixin],
  name: 'DealerCard',
  components: {
    OneButton
  },
  props: {
    title: {
      type: String,
      required: false,
      default: ""
    },
    dealer: {
      type: Object,
      required: false,
      default: null
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: [
    "onDeleteDealerClick",
    "onContactDealerButtonClick"
  ],
  methods: {
    removeHttpSubtring(url) {
      url = url.replace("http://", "");
      url = url.replace("https://", "");

      return url;
    },
    onDeleteDealerClick(clickedDeleteButtonId) {
      this.$emit("onDeleteDealerClick", clickedDeleteButtonId);
    },
    onContactDealerButtonClick() {
      this.$emit("onContactDealerButtonClick");
    }
  }
}
</script>

<style lang="scss" scoped>
.card{
  background: $grey6;
  padding: 1.5rem;

  &.__m {
    .disabled {
      opacity: 0.4;
    }

    .dealer-card-name {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .dealer-card-name-text {
        font-family: 'MINISerif';
        font-weight: 700;
        font-size: 22px;
        line-height: 22px;
        color: $bmwcolor;

        @media screen and (min-width: 769px) {
          font-size: 24px;
          line-height: 24px;
        }
      }

      .dealer-card-name-delete {
        background-image: url('~@/assets/mini/light/delete-mini-light.svg');
        background-repeat: no-repeat;
        background-position: center;
        width: 24px;
        height: 24px;
        margin-left: 1rem;
        cursor: pointer;

        &:hover {
          outline: auto;
          outline-color: $bmwcolor;
        }
      }
    }

    .dealer-card-street {
      display: block;
      font-family: MINISansSerif;
      font-size: 16px;
      line-height: 24px;
      margin-top: 0.5rem;
      margin-bottom: 2rem;
      color: $bmwcolor;

      @media screen and (min-width: 768px) {
        display: flex;
        align-items: center;
      }

      .dealer-card-street-separator {
        display: none;
        margin-left: 0.5rem;
        margin-right: 0.5rem;

        @media screen and (min-width: 768px){
          display: block;
        }
      }
    }

    .dealer-card-phone {
      display: flex;
      align-items: center;
      margin-bottom: 1rem;

      .dealer-card-phone-icon {
        background-image: url("@/assets/mini/light/dealer-phone-mini-light.svg");
        width: 24px;
        height: 24px;
        margin-right: 0.5rem;
      }

      .dealer-card-phone-text {
        font-family: MINISansSerif;
        font-size: 14px;
        line-height: 18px;
        color: $bmwcolor;

        @media screen and (min-width: 769px) {
          font-size: 16px;
          line-height: 20px;
        }
      }
    }

    .dealer-card-fax {
      display: flex;
      align-items: center;
      margin-bottom: 1rem;

      .dealer-card-fax-icon {
        background-image: url("@/assets/mini/light/print-mini-light.svg");
        width: 24px;
        height: 24px;
        margin-right: 0.5rem;
      }

      .dealer-card-fax-text {
        font-family: MINISansSerif;
        font-size: 14px;
        line-height: 18px; 
        color: $bmwcolor;

        @media screen and (min-width: 769px) {
          font-size: 16px;
          line-height: 20px;
        }
      }
    }

    .dealer-card-mail {
      display: flex;
      align-items: center;
      margin-bottom: 1rem;

      .dealer-card-mail-icon {
        background-image: url("@/assets/mini/light/dealer-email-mini-light.svg");
        width: 24px;
        height: 24px;
        margin-right: 0.5rem;
      }

      .dealer-card-mail-text {
        font-family: MINISansSerif;
        font-size: 14px;
        line-height: 18px;
        color: $bmwcolor;

        @media screen and (min-width: 769px) {
          font-size: 16px;
          line-height: 20px;
        }
      }
    }

    .dealer-card-homepage {
      display: flex;
      align-items: center;

      .dealer-card-homepage-icon {
        background-image: url("@/assets/mini/light/globe-mini-light.svg");
        width: 24px;
        height: 24px;
        margin-right: 0.5rem;
      }

      .dealer-card-homepage-text {
        font-family: MINISansSerif;
        font-size: 14px;
        line-height: 18px;

        @media screen and (min-width: 769px) {
          font-size: 16px;
          line-height: 20px;
        }

        a, div {
          &.dealer-card-homepage-link {
            color: $bmwcolor !important;
            font-weight: normal !important;

            &:hover {
              font-weight: bold !important;
              text-decoration: underline !important;
            }
          }

          &.dealer-card-homepage-link-disabled {
            color: $bmwcolor !important;
            font-weight: normal !important;
          }
        }
      }
    }

    .buttonsContainer {
      margin-top: 2rem;

      .custom-button {
        &._profileStyle {
          // width: 100%;
          width: auto !important;
          min-width: 180px;
          padding-left: 2rem;
          padding-right: 2rem;
          margin-bottom: 0;
          padding-bottom: 3px;

          // TODO: de-comment this style block once new design for MINI is approved
          /* background: transparent;
          color: black;
          border: 2px solid black;
          border-radius: 2rem;
          font-family: MINISansSerif;
          font-size: 18px;
          font-weight: 400;
          line-height: 24px;
          text-transform: none;

          &:hover {
            border: 3px solid black;
          } */

          &:disabled {
            cursor: default;

            &:hover {
              color: #999999;
              background-color: #e5e5e5;
              border: 2px solid #e5e5e5;
            }
          }
        }
      }
    }

    &.__d {
      .buttonsContainer {
        .custom-button {
          &._profileStyle {
            border: 2px solid $minidarkmodebg;
            background: $minidarkmodebg;
            color: white;
            padding-bottom: 3px;

            &:not(:disabled) {
              &:hover {
                background: transparent;
                border: 3px solid $minidarkmodebg;
                color: $minidarkmodebg;
              }
            }

            &:disabled {
              opacity: 0.4;
              cursor: default;

              border: 2px solid $minidarkmodebg;
              background: $minidarkmodebg;
              color: white;
            }
          }
        }
      }
    }
  }
}
</style>
